/**
 * Schema Service for dev-tools
 * Part of Phase 2 refactoring - Service Layer (Business Logic)
 *
 * Handles schema extraction and method definition management
 */
import { AVAILABLE_SERVICES } from "../constants";
import { FieldSchema, MethodDefinition, ServiceInfo } from "../types";
import { logger } from "../utils/logger";
import { ProtobufSchemaExtractor } from "../utils/schema-extractor";

export class SchemaService {
  private static methodCache: Map<string, MethodDefinition[]> = new Map();
  private static singleMethodCache: Map<string, MethodDefinition> = new Map();

  /**
   * Get all available services
   */
  static getAvailableServices(): ServiceInfo[] {
    return AVAILABLE_SERVICES.map((service) => {
      const methodCount = this.getMethodCountForService(service.id);
      return {
        id: service.id,
        name: service.name,
        description: service.description,
        methodCount,
      };
    });
  }

  /**
   * Get method count for a specific service
   */
  private static getMethodCountForService(serviceId: string): number {
    try {
      const serviceDefinition = AVAILABLE_SERVICES.find(
        (s) => s.id === serviceId
      );
      if (!serviceDefinition?.service?.methods) {
        return 0;
      }
      return Object.keys(serviceDefinition.service.methods).length;
    } catch (error) {
      logger.warn(`Failed to get method count for service ${serviceId}`, {
        error,
      });
      return 0;
    }
  }

  /**
   * Get all available method definitions with their schemas for a specific service
   */
  static async getMethodDefinitionsForService(
    serviceId: string
  ): Promise<MethodDefinition[]> {
    try {
      // Check cache first
      const cached = this.methodCache.get(serviceId);
      if (cached) {
        return cached;
      }

      const serviceDefinition = AVAILABLE_SERVICES.find(
        (s) => s.id === serviceId
      );
      if (!serviceDefinition) {
        throw new Error(`Service ${serviceId} not found`);
      }

      const methodSchemas =
        ProtobufSchemaExtractor.extractMethodSchemasForService(
          serviceDefinition.service,
          serviceId
        );

      const methodDefinitions: MethodDefinition[] = methodSchemas.map(
        (schema) => ({
          name: schema.name,
          description: schema.description,
          inputSchema: schema.fields,
          category: this.categorizeMethod(schema.name),
          serviceId: serviceId,
          // Legacy compatibility
          fullName: schema.name,
          inputType: `${schema.name}Request`,
          outputType: `${schema.name}Response`,
          schema: {
            fields: schema.fields.map((field) => ({
              name: field.name,
              type: field.type,
              label: (field.isOptional ? "optional" : "required") as
                | "optional"
                | "required",
              description: field.description,
              isEnum: field.type === "enum",
              isMessage: field.type === "message",
              enumValues: field.enumValues,
            })),
            enums: [],
            nestedTypes: [],
          },
        })
      );

      // Cache the results
      this.methodCache.set(serviceId, methodDefinitions);

      return methodDefinitions;
    } catch (error) {
      logger.error(
        `Failed to extract method schemas for service ${serviceId}`,
        { error }
      );
      throw new Error(`Failed to load method definitions for ${serviceId}`);
    }
  }

  /**
   * Get all available method definitions with their schemas (legacy method for support service)
   */
  static async getMethodDefinitions(): Promise<MethodDefinition[]> {
    return this.getMethodDefinitionsForService("support");
  }

  /**
   * Get a specific method definition by name
   */
  static async getMethodDefinition(
    methodName: string
  ): Promise<MethodDefinition | null> {
    if (this.singleMethodCache.has(methodName)) {
      return this.singleMethodCache.get(methodName) || null;
    }

    const methods = await this.getMethodDefinitions();
    const method = methods.find((m) => m.name === methodName);

    if (method) {
      this.singleMethodCache.set(methodName, method);
    }

    return method || null;
  }

  /**
   * Get a specific field schema from a method
   */
  static getFieldSchema(
    methodName: string,
    fieldName: string
  ): FieldSchema | null {
    const method = this.singleMethodCache.get(methodName);
    if (!method) return null;

    return (
      method.inputSchema.find(
        (field: FieldSchema) => field.name === fieldName
      ) || null
    );
  }

  /**
   * Categorize methods for better organization
   */
  private static categorizeMethod(methodName: string): string {
    const categories: Record<string, string[]> = {
      "User Management": [
        "searchUsers",
        "createUser",
        "updateUser",
        "deleteUser",
        "getUserProfile",
        "listUsers",
        "activateUser",
        "deactivateUser",
      ],
      Subscription: [
        "createSubscription",
        "updateSubscription",
        "cancelSubscription",
        "getSubscription",
        "listSubscriptions",
        "renewSubscription",
      ],
      Organization: [
        "createOrganization",
        "updateOrganization",
        "deleteOrganization",
        "getOrganization",
        "listOrganizations",
        "transferOrganization",
      ],
      Billing: [
        "createInvoice",
        "updatePaymentMethod",
        "processPayment",
        "getPaymentMethod",
        "listInvoices",
        "refundPayment",
      ],
      Security: [
        "authenticateUser",
        "authorizeAction",
        "revokeAccess",
        "listPermissions",
        "updatePermissions",
      ],
      Analytics: [
        "getUsageStats",
        "generateReport",
        "exportData",
        "getMetrics",
        "trackEvent",
      ],
    };

    for (const [category, methods] of Object.entries(categories)) {
      if (
        methods.some((method) =>
          methodName.toLowerCase().includes(method.toLowerCase())
        )
      ) {
        return category;
      }
    }

    return "Other";
  }

  /**
   * Get methods by category
   */
  static async getMethodsByCategory(
    category: string
  ): Promise<MethodDefinition[]> {
    const allMethods = await this.getMethodDefinitions();
    return allMethods.filter((method) => method.category === category);
  }

  /**
   * Search methods by name or description
   */
  static async searchMethods(query: string): Promise<MethodDefinition[]> {
    const allMethods = await this.getMethodDefinitions();
    const lowercaseQuery = query.toLowerCase();

    return allMethods.filter(
      (method) =>
        method.name.toLowerCase().includes(lowercaseQuery) ||
        (method.description &&
          method.description.toLowerCase().includes(lowercaseQuery))
    );
  }

  /**
   * Clear the method cache
   */
  static clearCache(): void {
    this.methodCache.clear();
    this.singleMethodCache.clear();
  }

  /**
   * Get available categories
   */
  static async getAvailableCategories(): Promise<string[]> {
    const allMethods = await this.getMethodDefinitions();
    const categories = new Set(
      allMethods.map((method) => method.category || "Other")
    );
    return Array.from(categories).sort();
  }

  /**
   * Validate if a method exists
   */
  static async validateMethodExists(methodName: string): Promise<boolean> {
    try {
      const method = await this.getMethodDefinition(methodName);
      return !!method;
    } catch {
      return false;
    }
  }
}
